import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Home, LogOut, Building, FileText, Eye, Phone, Mail } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_deal-dashboard-16/artifacts/mtal5hnl_0e8de284-294a-4a25-8f18-7d7cab9aad20.png";

const ClientDashboard = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [propertiesRes, statsRes] = await Promise.all([
        axios.get(`${API}/properties`, { headers: getAuthHeaders() }),
        axios.get(`${API}/stats`, { headers: getAuthHeaders() })
      ]);
      setProperties(propertiesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-orange border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-dashboard">
      {/* Header */}
      <header className="bg-brand-primary text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Mitten Deals" className="h-10 w-auto" />
              <span className="hidden sm:block text-sm font-medium text-slate-300">Client Portal</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-300">Welcome, {user?.name}</span>
              <Button onClick={handleLogout} variant="ghost" className="text-white hover:bg-white/10" data-testid="logout-button">
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="card-surface p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center">
                <Building className="w-6 h-6 text-brand-orange" />
              </div>
              <div>
                <p className="text-3xl font-bold text-brand-primary">{stats.my_properties || 0}</p>
                <p className="text-sm text-slate-500">Available Properties</p>
              </div>
            </div>
          </div>
          <div className="card-surface p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-brand-primary">{stats.my_contracts || 0}</p>
                <p className="text-sm text-slate-500">Under Contract</p>
              </div>
            </div>
          </div>
        </div>

        {/* Properties */}
        <div className="mb-6">
          <h2 className="text-xl font-bold text-brand-primary mb-4">My Properties</h2>
          
          {properties.length === 0 ? (
            <div className="card-surface p-12 text-center">
              <Building className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-brand-primary mb-2">No Properties Available</h3>
              <p className="text-slate-500 mb-4">
                You don't have any properties assigned yet. Contact us to discuss available opportunities.
              </p>
              <div className="flex items-center justify-center gap-4">
                <a href="tel:18888714928" className="flex items-center gap-2 text-brand-orange font-semibold hover:underline">
                  <Phone className="w-4 h-4" /> 1 (888) 871-4928
                </a>
                <a href="mailto:admin@mittendeals.live" className="flex items-center gap-2 text-brand-orange font-semibold hover:underline">
                  <Mail className="w-4 h-4" /> Email Us
                </a>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {properties.map(property => (
                <div key={property.id} className="card-surface property-card overflow-hidden cursor-pointer" onClick={() => navigate(`/property/${property.id}`)} data-testid={`property-card-${property.id}`}>
                  <div className="h-48 bg-slate-200 relative">
                    {property.images?.length > 0 ? (
                      <img 
                        src={`${API}/files/${property.images[0].storage_path}?auth=${localStorage.getItem("token")}`}
                        alt={property.address}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400">
                        <Home className="w-16 h-16" />
                      </div>
                    )}
                    <div className={`absolute top-3 right-3 px-3 py-1.5 rounded-full text-xs font-bold ${
                      property.under_contract ? "bg-yellow-500 text-white" : "bg-green-500 text-white"
                    }`}>
                      {property.under_contract ? "Under Contract" : "Available"}
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-semibold text-brand-primary text-lg">{property.address}</h3>
                    <p className="text-slate-500">{property.city}, {property.state} {property.zip_code}</p>
                    <p className="text-2xl font-bold text-brand-orange mt-3">${property.price?.toLocaleString()}</p>
                    <div className="flex items-center gap-4 text-sm text-slate-500 mt-3">
                      <span>{property.bedrooms} bed</span>
                      <span>{property.bathrooms} bath</span>
                      <span>{property.sqft?.toLocaleString()} sqft</span>
                    </div>
                    <Button className="btn-primary w-full mt-4" data-testid={`view-property-${property.id}`}>
                      <Eye className="w-4 h-4 mr-2" /> View Details
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Contact Info */}
        <div className="card-surface p-6 mt-8">
          <h3 className="font-semibold text-brand-primary mb-4">Need Assistance?</h3>
          <div className="flex flex-wrap gap-6">
            <a href="tel:18888714928" className="flex items-center gap-3 text-slate-600 hover:text-brand-orange transition-colors">
              <div className="w-10 h-10 bg-brand-orange/10 rounded-lg flex items-center justify-center">
                <Phone className="w-5 h-5 text-brand-orange" />
              </div>
              <div>
                <p className="text-xs text-slate-400">Call Us</p>
                <p className="font-semibold">1 (888) 871-4928</p>
              </div>
            </a>
            <a href="mailto:admin@mittendeals.live" className="flex items-center gap-3 text-slate-600 hover:text-brand-orange transition-colors">
              <div className="w-10 h-10 bg-brand-orange/10 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-brand-orange" />
              </div>
              <div>
                <p className="text-xs text-slate-400">Email Us</p>
                <p className="font-semibold">admin@mittendeals.live</p>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientDashboard;
